import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X, Pill, ToggleLeft, ToggleRight, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { cn } from '@/lib/utils';
import { ComboboxInput } from '@/components/ui/ComboboxInput';
import { VademecumPickerInline, type MedicamentoSeleccionado } from '@/components/vademecum/VademecumPicker';
import { getErrorMessage } from '@/lib/errors';
import { useConfiguracionClinica, moduloAplica } from '@/hooks/useConfiguracionClinica';
import { useFacultadPrescribir } from '@/hooks/useFacultadPrescribir';

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white';

const DOSIS_RAPIDAS = ['1/2 tableta', '1 tableta', '2 tabletas', '1 cápsula', '2 cápsulas', '1 ampolleta', '5 ML', '10 ML', '1 gota', '2 gotas'];
const FRECUENCIAS_RAPIDAS = ['cada hora', 'cada 4 horas', 'cada 6 horas', 'cada 8 horas', 'cada 12 horas', 'cada 24 horas'];

const emptyForm = {
  vademecumId: '', nombreMedicamento: '', dosis: '', frecuencia: '',
  via: '', indicacion: '', fechaInicio: '', fechaFin: '', notas: '', activo: true,
};

interface Props { id: string; canEdit: boolean }

export function MedicamentosActivosTab({ id, canEdit }: Props) {
  const qc = useQueryClient();
  const { perfilTipo, modulosHabilitados } = useConfiguracionClinica();
  const { tieneFacultadPrescribir } = useFacultadPrescribir();
  const puedeEditar = canEdit && moduloAplica('recetas_emitir', perfilTipo, modulosHabilitados) && tieneFacultadPrescribir; // modo solo-lectura sin facultad de prescribir
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [mostrarInactivos, setMostrarInactivos] = useState(false);

  const { data: medicamentos = [], isLoading } = useQuery({
    queryKey: ['medicamentos-activos', id],
    queryFn: () => api.get(`/api/pacientes/${id}/medicamentos-activos`).then(r => r.data as any[]),
  });

  const createMutation = useMutation({
    mutationFn: (dto: any) => api.post(`/api/pacientes/${id}/medicamentos-activos`, dto).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['medicamentos-activos', id] }); toast.success('Medicamento agregado'); closeModal(); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al guardar')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ medId, dto }: any) => api.patch(`/api/pacientes/${id}/medicamentos-activos/${medId}`, dto).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['medicamentos-activos', id] }); toast.success('Medicamento actualizado'); closeModal(); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al actualizar')),
  });

  const deleteMutation = useMutation({
    mutationFn: (medId: string) => api.delete(`/api/pacientes/${id}/medicamentos-activos/${medId}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['medicamentos-activos', id] }); toast.success('Medicamento eliminado'); },
  });

  function openAdd() { setForm({ ...emptyForm }); setEditingId(null); setModalOpen(true); }
  function openEdit(m: any) {
    setForm({
      vademecumId: m.vademecumId || '', nombreMedicamento: m.nombreMedicamento || '',
      dosis: m.dosis || '', frecuencia: m.frecuencia || '', via: m.via || '',
      indicacion: m.indicacion || '', fechaInicio: m.fechaInicio ? m.fechaInicio.split('T')[0] : '',
      fechaFin: m.fechaFin ? m.fechaFin.split('T')[0] : '', notas: m.notas || '', activo: m.activo,
    });
    setEditingId(m.id);
    setModalOpen(true);
  }
  function closeModal() { setModalOpen(false); setEditingId(null); }

  function f(field: string) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(prev => ({ ...prev, [field]: e.target.value }));
  }

  function handleVademecumSelect(sel: MedicamentoSeleccionado) {
    setForm(prev => ({
      ...prev,
      vademecumId: sel.vademecumId,
      nombreMedicamento: sel.nombreGenerico,
      dosis: sel.dosisSugerida || prev.dosis,
      frecuencia: sel.frecuenciaSugerida || prev.frecuencia,
      via: sel.viaAdministracion || prev.via,
    }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.nombreMedicamento) return toast.error('El nombre del medicamento es requerido');
    const dto = { ...form, fechaInicio: form.fechaInicio || null, fechaFin: form.fechaFin || null };
    if (editingId) updateMutation.mutate({ medId: editingId, dto });
    else createMutation.mutate(dto);
  }

  function toggleActivo(m: any) {
    updateMutation.mutate({ medId: m.id, dto: { activo: !m.activo } });
  }

  const visibles = medicamentos.filter((m: any) => mostrarInactivos || m.activo);
  const activos = medicamentos.filter((m: any) => m.activo).length;
  const medicamentosNombres = medicamentos.filter((m: any) => m.activo).map((m: any) => m.nombreMedicamento);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <p className="text-xs text-text-secondary">
            <span className="font-semibold text-text-primary">{activos}</span> activo{activos !== 1 ? 's' : ''}
            {medicamentos.length > activos && ` · ${medicamentos.length - activos} inactivo${medicamentos.length - activos !== 1 ? 's' : ''}`}
          </p>
          {medicamentos.some((m: any) => !m.activo) && (
            <button type="button" onClick={() => setMostrarInactivos(p => !p)}
              className="text-xs text-primary-600 hover:underline">
              {mostrarInactivos ? 'Ocultar inactivos' : 'Ver inactivos'}
            </button>
          )}
        </div>
        {puedeEditar && (
          <button type="button" onClick={openAdd}
            className="flex items-center gap-1.5 bg-primary-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-primary-700 transition-colors">
            <Plus size={13} /> Agregar medicamento
          </button>
        )}
      </div>

      {/* List */}
      {isLoading ? (
        <p className="text-center py-10 text-sm text-text-secondary">Cargando...</p>
      ) : visibles.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200">
          <Pill size={28} className="mx-auto text-gray-300 mb-2" />
          <p className="text-sm text-text-secondary">Sin medicamentos activos registrados</p>
        </div>
      ) : (
        <div className="space-y-2">
          {visibles.map((m: any) => (
            <div key={m.id} className={cn('rounded-xl border p-3.5 transition-opacity', m.activo ? 'border-gray-100 bg-white' : 'border-gray-100 bg-gray-50 opacity-60')}>
              <div className="flex items-start gap-3">
                <div className={cn('w-2 h-2 rounded-full flex-shrink-0 mt-1.5', m.activo ? 'bg-green-500' : 'bg-gray-400')} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-text-primary text-sm">{m.nombreMedicamento}</p>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                        {m.dosis && <span className="text-xs text-text-secondary">{m.dosis}</span>}
                        {m.frecuencia && <span className="text-xs text-text-secondary">{m.frecuencia}</span>}
                        {m.via && <span className="text-xs text-text-secondary capitalize">{m.via.toLowerCase()}</span>}
                      </div>
                      {m.indicacion && <p className="text-xs text-text-secondary mt-1 italic">{m.indicacion}</p>}
                    </div>
                    {puedeEditar && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button type="button" onClick={() => toggleActivo(m)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary transition-colors" title={m.activo ? 'Marcar inactivo' : 'Activar'}>
                          {m.activo ? <ToggleRight size={16} className="text-green-600" /> : <ToggleLeft size={16} />}
                        </button>
                        <button type="button" onClick={() => openEdit(m)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary transition-colors">
                          <Pencil size={13} />
                        </button>
                        <button type="button" onClick={() => deleteMutation.mutate(m.id)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-text-secondary hover:text-red-500 transition-colors">
                          <X size={13} />
                        </button>
                      </div>
                    )}
                  </div>
                  {m.notas && <p className="text-xs text-text-secondary mt-1.5 bg-gray-50 rounded-lg px-2.5 py-1.5">{m.notas}</p>}
                  {m.medico && <p className="text-[10px] text-text-secondary mt-1.5">Dr. {m.medico.nombre}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="font-semibold text-text-primary">{editingId ? 'Editar medicamento' : 'Agregar medicamento activo'}</h3>
              <button type="button" onClick={closeModal} className="p-1 rounded-lg hover:bg-gray-100 text-text-secondary"><X size={18} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {!editingId && (
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Buscar en vademecum clínico</label>
                  <VademecumPickerInline
                    medicamentosActivos={medicamentosNombres}
                    onSelect={handleVademecumSelect}
                  />
                  <p className="text-[11px] text-text-secondary mt-1">O escribe manualmente el nombre a continuación</p>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Nombre del medicamento *</label>
                <input required className={inputCls} value={form.nombreMedicamento}
                  onChange={f('nombreMedicamento')} placeholder="Nombre del medicamento" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Dosis</label>
                  <ComboboxInput options={DOSIS_RAPIDAS} value={form.dosis}
                    onChange={v => setForm(prev => ({ ...prev, dosis: v }))}
                    placeholder="ej: 1 tableta" className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Frecuencia</label>
                  <ComboboxInput options={FRECUENCIAS_RAPIDAS} value={form.frecuencia}
                    onChange={v => setForm(prev => ({ ...prev, frecuencia: v }))}
                    placeholder="ej: cada 8 horas" className={inputCls} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Vía de administración</label>
                <input className={inputCls} value={form.via} onChange={f('via')} placeholder="ej: Oral, IV, tópica..." />
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Indicación</label>
                <input className={inputCls} value={form.indicacion} onChange={f('indicacion')} placeholder="Motivo del medicamento" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Fecha inicio</label>
                  <input type="date" className={inputCls} value={form.fechaInicio} onChange={f('fechaInicio')} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Fecha fin (si aplica)</label>
                  <input type="date" className={inputCls} value={form.fechaFin} onChange={f('fechaFin')} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Notas adicionales</label>
                <textarea rows={2} className={`${inputCls} resize-none`} value={form.notas} onChange={f('notas')} placeholder="Observaciones..." />
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <div
                  onClick={() => setForm(prev => ({ ...prev, activo: !prev.activo }))}
                  className={cn('w-9 h-5 rounded-full transition-colors relative', form.activo ? 'bg-green-500' : 'bg-gray-300')}
                >
                  <span className={cn('absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform', form.activo ? 'translate-x-4' : 'translate-x-0.5')} />
                </div>
                <span className="text-sm text-text-primary">Medicamento activo</span>
              </label>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeModal} className="flex-1 border border-gray-200 text-text-secondary py-2 rounded-lg text-sm hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={createMutation.isPending || updateMutation.isPending}
                  className="flex-1 bg-primary-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-60">
                  {(createMutation.isPending || updateMutation.isPending) ? 'Guardando...' : editingId ? 'Guardar cambios' : 'Agregar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
